import { useEffect, useState } from "react";
import {
  getCJWDICostFileStatus,
  getCJWDICostFileStatuses,
  getCJWDICostFileStatusesByBatch,
} from "../API/CJWDICostFileStatusApi";

const unavailableFor = (row) => ({
  available: false,
  approvedAvailable: false,
  draftAvailable: false,
  batchId: row.batchId,
  reason: "UNAVAILABLE",
});

export const useCJWDICostFileAvailability = (rows = [], enabled = false) => {
  const [availabilityByFileId, setAvailabilityByFileId] = useState({});

  useEffect(() => {
    let active = true;

    if (!enabled || !Array.isArray(rows)) {
      setAvailabilityByFileId({});
      return () => {
        active = false;
      };
    }

    const candidates = rows.filter(
      (row) => Boolean(row?.fileId) && Boolean(row?.batchId)
    );

    setAvailabilityByFileId({});
    if (candidates.length === 0) {
      return () => {
        active = false;
      };
    }

    const loadAvailability = async () => {
      let availability;
      try {
        const batchResult = await getCJWDICostFileStatusesByBatch(
          [...new Set(candidates.map((row) => row.batchId))]
        );
        availability = Object.fromEntries(
          candidates.map((row) => [
            row.fileId,
            batchResult?.[row.batchId] || unavailableFor(row),
          ])
        );
      } catch (error) {
        if (error?.status === 404 || error?.status === 405) {
          // Rolling-deployment compatibility with backends that predate exact batch links.
          try {
            const legacyBatchResult = await getCJWDICostFileStatuses(
              candidates.map((row) => row.fileId)
            );
            availability = Object.fromEntries(
              candidates.map((row) => [
                row.fileId,
                legacyBatchResult?.[row.fileId] || unavailableFor(row),
              ])
            );
          } catch (legacyBatchError) {
            if (legacyBatchError?.status === 404 || legacyBatchError?.status === 405) {
              const entries = await Promise.all(candidates.map(async (row) => {
                try {
                  return [row.fileId, await getCJWDICostFileStatus(row.fileId)];
                } catch (legacyError) {
                  console.error(
                    `Failed to load cost-file readiness for CJW-DI file ${row.fileId}:`,
                    legacyError
                  );
                  return [row.fileId, unavailableFor(row)];
                }
              }));
              availability = Object.fromEntries(entries);
            } else {
              console.error("Failed to load legacy CJW-DI cost-file readiness:", legacyBatchError);
              availability = Object.fromEntries(
                candidates.map((row) => [row.fileId, unavailableFor(row)])
              );
            }
          }
        } else {
          console.error("Failed to load CJW-DI cost-file readiness:", error);
          availability = Object.fromEntries(
            candidates.map((row) => [row.fileId, unavailableFor(row)])
          );
        }
      }

      if (active) {
        setAvailabilityByFileId(availability);
      }
    };

    loadAvailability();

    return () => {
      active = false;
    };
  }, [enabled, rows]);

  return availabilityByFileId;
};

export default useCJWDICostFileAvailability;
